import { addDoc, getDocs, getDoc, doc, updateDoc, deleteDoc, query, where, collection, serverTimestamp, arrayUnion, orderBy, increment } from "firebase/firestore";
import { db } from "../config/FirebaseConfig";
import ImageKitApi from "./ImageKitApi";

class DocumentApi {
    constructor() {
        this.imageKitApi = new ImageKitApi();
    }

    async createDocument(body = {}) {
        if (!body || !body.title || !body.url || !body.ownerId || !body.projectId) {
            console.error("Invalid document data:", body);
            return { success: false, error: "Invalid document data" };
        }
        try {
            const docRef = await addDoc(collection(db, "documents"), {
                title: body.title,
                description: body.description || null,
                // Original document (for producers and paid consumers)
                originalUrl: body.originalUrl || body.url || null,
                originalFilePath: body.originalFilePath || body.filePath || null,
                originalFileId: body.originalFileId || body.fileId || null,
                // Watermarked document (for unpaid consumers)
                watermarkedUrl: body.watermarkedUrl || null,
                watermarkedFilePath: body.watermarkedFilePath || null,
                watermarkedFileId: body.watermarkedFileId || null,
                // File metadata
                fileType: body.fileType || null,
                fileName: body.fileName || null,
                ownerId: body.ownerId,
                owner: body.owner || null,
                companyId: body.companyId || null,
                company: body.company || null,
                projectId: body.projectId || null,
                isDeleted: false,
                deletedAt: null,
                // Simplified status: 'unpaid' or 'paid'
                status: 'unpaid',
                // View tracking
                viewCount: 0,
                uniqueViewerCount: 0,
                firstViewedAt: null,
                lastViewedAt: null,
                viewedBy: [], // Keep for backward compatibility
                // Payment tracking
                paidAt: null,
                paidMarkedBy: null,
                // Download tracking (separate from status)
                downloaded: false,
                downloadedAt: null,
                downloadedBy: null,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });

            return { success: true, documentId: docRef.id };
        } catch (error) {
            console.error("Error creating document:", error);
            return { success: false, error: error.message };
        }
    }

    async getDocumentsByProjectId(projectId, includeDeleted = false) {
        if (!projectId) {
            console.error("Invalid project ID:", projectId);
            return { success: false, error: "Invalid project ID" };
        }
        try {
            let q;
            if (includeDeleted) {
                q = query(
                    collection(db, "documents"),
                    where("projectId", "==", projectId)
                );
            } else {
                q = query(
                    collection(db, "documents"),
                    where("projectId", "==", projectId),
                    where("isDeleted", "==", false)
                );
            }
            const querySnapshot = await getDocs(q);
            const documents = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            return { success: true, data: documents };
        } catch (error) {
            console.error("Error fetching documents:", error);
            return { success: false, error: error.message };
        }
    }

    async getDeletedDocumentsByProjectId(projectId) {
        if (!projectId) {
            console.error("Invalid project ID:", projectId);
            return { success: false, error: "Invalid project ID" };
        }
        try {
            const q = query(
                collection(db, "documents"),
                where("projectId", "==", projectId),
                where("isDeleted", "==", true)
            );
            const querySnapshot = await getDocs(q);
            const documents = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            return { success: true, data: documents };
        } catch (error) {
            console.error("Error fetching deleted documents:", error);
            return { success: false, error: error.message };
        }
    }

    async getDocument(documentId) {
        if (!documentId) {
            console.error("Invalid document ID:", documentId);
            return { success: false, error: "Invalid document ID" };
        }
        try {
            const docRef = doc(db, "documents", documentId);
            const docSnap = await getDoc(docRef);
            if (!docSnap.exists()) {
                return { success: false, error: "Document not found" };
            }
            return { success: true, document: { id: docSnap.id, ...docSnap.data() } };
        } catch (error) {
            console.error("Error fetching document:", error);
            return { success: false, error: error.message };
        }
    }

    async softDeleteDocument(documentId) {
        if (!documentId) {
            console.error("Invalid document ID:", documentId);
            return { success: false, error: "Invalid document ID" };
        }

        try {
            // Update Firestore document (file stays on ImageKit)
            const docRef = doc(db, "documents", documentId);
            await updateDoc(docRef, {
                isDeleted: true,
                deletedAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });

            return { success: true, message: "Document moved to trash" };
        } catch (error) {
            console.error("Error soft deleting document:", error);
            return { success: false, error: error.message };
        }
    }

    async hardDeleteDocument(documentId) {
        if (!documentId) {
            console.error("Invalid document ID:", documentId);
            return { success: false, error: "Invalid document ID" };
        }

        try {
            // Get the document first
            const docResult = await this.getDocument(documentId);
            if (!docResult.success) {
                return docResult;
            }

            const document = docResult.document;
            const deleteErrors = [];

            // Delete original file from ImageKit
            if (document.originalFileId) {
                const originalDeleteResult = await this.imageKitApi.deleteFile(document.originalFileId);
                if (!originalDeleteResult.success) {
                    console.warn("Failed to delete original file:", originalDeleteResult.error);
                    deleteErrors.push("original");
                }
            }

            // Delete watermarked file from ImageKit
            if (document.watermarkedFileId) {
                const watermarkedDeleteResult = await this.imageKitApi.deleteFile(document.watermarkedFileId);
                if (!watermarkedDeleteResult.success) {
                    console.warn("Failed to delete watermarked file:", watermarkedDeleteResult.error);
                    deleteErrors.push("watermarked");
                }
            }

            // Delete document from Firestore completely
            const docRef = doc(db, "documents", documentId);
            await deleteDoc(docRef);

            return {
                success: true,
                message: deleteErrors.length === 0
                    ? "Document permanently deleted"
                    : `Document deleted (failed to remove ${deleteErrors.join(" and ")} file(s) from storage)`
            };
        } catch (error) {
            console.error("Error hard deleting document:", error);
            return { success: false, error: error.message };
        }
    }

    async restoreDocument(documentId) {
        if (!documentId) {
            console.error("Invalid document ID:", documentId);
            return { success: false, error: "Invalid document ID" };
        }

        try {
            // Get the document first to check its state
            const docResult = await this.getDocument(documentId);
            if (!docResult.success) {
                return docResult;
            }

            const document = docResult.document;

            if (!document.isDeleted) {
                return { success: false, error: "Document is not deleted" };
            }

            if (document.isPermanentlyDeleted) {
                return { success: false, error: "Document has been permanently deleted and cannot be restored" };
            }

            // Update Firestore document (file never moved on ImageKit)
            const docRef = doc(db, "documents", documentId);
            await updateDoc(docRef, {
                isDeleted: false,
                deletedAt: null,
                updatedAt: serverTimestamp(),
            });

            return { success: true, message: "Document restored successfully" };
        } catch (error) {
            console.error("Error restoring document:", error);
            return { success: false, error: error.message };
        }
    }

    async setWatermarkedVersion(documentId, watermarkedData) {
        if (!documentId || !watermarkedData) {
            console.error("Invalid document ID or watermarked data:", { documentId, watermarkedData });
            return { success: false, error: "Invalid document ID or watermarked data" };
        }

        try {
            const docRef = doc(db, "documents", documentId);
            await updateDoc(docRef, {
                watermarkedUrl: watermarkedData.url,
                watermarkedFilePath: watermarkedData.filePath,
                watermarkedFileId: watermarkedData.fileId,
                updatedAt: serverTimestamp(),
            });

            return { success: true, message: "Watermarked version added" };
        } catch (error) {
            console.error("Error setting watermarked version:", error);
            return { success: false, error: error.message };
        }
    }

    async getDocumentCountByUserId(userId) {
        if (!userId) {
            console.error("Invalid user ID:", userId);
            return { success: false, error: "Invalid user ID" };
        }
        try {
            const q = query(
                collection(db, "documents"),
                where("ownerId", "==", userId),
                where("isDeleted", "==", false)
            );
            const querySnapshot = await getDocs(q);
            return { success: true, count: querySnapshot.size };
        } catch (error) {
            console.error("Error fetching document count:", error);
            return { success: false, error: error.message };
        }
    }

    async recordDocumentView(documentId, userId, userInfo = {}, isProducer = false) {
        if (!documentId || !userId) {
            console.error("Invalid document ID or user ID:", { documentId, userId });
            return { success: false, error: "Invalid document ID or user ID" };
        }

        // Skip recording views for producers
        if (isProducer) {
            return { success: true, message: "Producer view not recorded" };
        }

        try {
            const docRef = doc(db, "documents", documentId);
            const docSnap = await getDoc(docRef);

            if (!docSnap.exists()) {
                return { success: false, error: "Document not found" };
            }

            const docData = docSnap.data();
            const viewedBy = docData.viewedBy || [];
            const isFirstViewByUser = !viewedBy.includes(userId);

            // Add entry to viewHistory subcollection
            const viewHistoryRef = collection(db, "documents", documentId, "viewHistory");
            await addDoc(viewHistoryRef, {
                userId,
                userEmail: userInfo.email || null,
                userName: userInfo.name || null,
                viewedAt: serverTimestamp(),
            });

            // Update document aggregates
            const updates = {
                viewCount: increment(1),
                lastViewedAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            };

            // Track first view timestamp
            if (!docData.firstViewedAt) {
                updates.firstViewedAt = serverTimestamp();
            }

            // Track unique viewers
            if (isFirstViewByUser) {
                updates.viewedBy = arrayUnion(userId);
                updates.uniqueViewerCount = increment(1);
            }

            await updateDoc(docRef, updates);
            return { success: true, message: "Document view recorded" };
        } catch (error) {
            console.error("Error recording document view:", error);
            return { success: false, error: error.message };
        }
    }

    async markDocumentAsPaid(documentId, markedBy) {
        if (!documentId || !markedBy) {
            console.error("Invalid document ID or marker ID:", { documentId, markedBy });
            return { success: false, error: "Invalid document ID or marker ID" };
        }

        try {
            const docRef = doc(db, "documents", documentId);
            const docSnap = await getDoc(docRef);

            if (!docSnap.exists()) {
                return { success: false, error: "Document not found" };
            }

            await updateDoc(docRef, {
                status: 'paid',
                paidAt: serverTimestamp(),
                paidMarkedBy: markedBy,
                updatedAt: serverTimestamp(),
            });

            return { success: true, message: "Document marked as paid" };
        } catch (error) {
            console.error("Error marking document as paid:", error);
            return { success: false, error: error.message };
        }
    }

    async recordDocumentDownload(documentId, userId) {
        if (!documentId || !userId) {
            console.error("Invalid document ID or user ID:", { documentId, userId });
            return { success: false, error: "Invalid document ID or user ID" };
        }

        try {
            const docRef = doc(db, "documents", documentId);
            const docSnap = await getDoc(docRef);

            if (!docSnap.exists()) {
                return { success: false, error: "Document not found" };
            }

            // Set downloaded flag without changing status
            await updateDoc(docRef, {
                downloaded: true,
                downloadedAt: serverTimestamp(),
                downloadedBy: userId,
                updatedAt: serverTimestamp(),
            });

            return { success: true, message: "Document download recorded" };
        } catch (error) {
            console.error("Error recording document download:", error);
            return { success: false, error: error.message };
        }
    }

    async getDocumentStats(projectId) {
        if (!projectId) {
            console.error("Invalid project ID:", projectId);
            return { success: false, error: "Invalid project ID" };
        }

        try {
            const q = query(
                collection(db, "documents"),
                where("projectId", "==", projectId),
                where("isDeleted", "==", false)
            );
            const querySnapshot = await getDocs(q);

            const stats = {
                total: 0,
                unpaid: 0,
                paid: 0,
                downloaded: 0,
                totalViews: 0,
            };

            querySnapshot.docs.forEach(docSnap => {
                const data = docSnap.data();
                stats.total++;

                // Normalize legacy statuses
                const status = this._normalizeStatus(data.status);
                if (status === 'unpaid') {
                    stats.unpaid++;
                } else if (status === 'paid') {
                    stats.paid++;
                }

                // Count downloads (separate flag)
                if (data.downloaded) {
                    stats.downloaded++;
                }

                // Sum total views
                stats.totalViews += data.viewCount || 0;
            });

            return { success: true, stats };
        } catch (error) {
            console.error("Error fetching document stats:", error);
            return { success: false, error: error.message };
        }
    }

    // Helper to normalize legacy status values
    _normalizeStatus(status) {
        if (status === 'uploaded' || status === 'viewed') {
            return 'unpaid';
        }
        if (status === 'downloaded') {
            return 'paid';
        }
        return status || 'unpaid';
    }

    async updateDocumentStatus(documentId, status, updatedBy) {
        if (!documentId || !status) {
            console.error("Invalid document ID or status:", { documentId, status });
            return { success: false, error: "Invalid document ID or status" };
        }

        const validStatuses = ['unpaid', 'paid'];
        if (!validStatuses.includes(status)) {
            return { success: false, error: "Invalid status value. Only 'unpaid' or 'paid' allowed." };
        }

        try {
            const docRef = doc(db, "documents", documentId);
            const updates = {
                status,
                updatedAt: serverTimestamp(),
            };

            // Set timestamp fields based on status
            if (status === 'paid') {
                updates.paidAt = serverTimestamp();
                updates.paidMarkedBy = updatedBy;
            }

            await updateDoc(docRef, updates);
            return { success: true, message: "Document status updated" };
        } catch (error) {
            console.error("Error updating document status:", error);
            return { success: false, error: error.message };
        }
    }

    async getDocumentViewHistory(documentId) {
        if (!documentId) {
            console.error("Invalid document ID:", documentId);
            return { success: false, error: "Invalid document ID" };
        }

        try {
            const viewHistoryRef = collection(db, "documents", documentId, "viewHistory");
            const q = query(viewHistoryRef, orderBy("viewedAt", "desc"));
            const querySnapshot = await getDocs(q);

            const history = querySnapshot.docs.map(docSnap => ({
                id: docSnap.id,
                ...docSnap.data(),
            }));

            return { success: true, history };
        } catch (error) {
            console.error("Error fetching view history:", error);
            return { success: false, error: error.message };
        }
    }

    async getDocumentsByUserParticipation(userId) {
        if (!userId) {
            console.error("Invalid user ID:", userId);
            return { success: false, error: "Invalid user ID" };
        }

        try {
            // Get documents where user is in viewedBy array
            const q = query(
                collection(db, "documents"),
                where("viewedBy", "array-contains", userId),
                where("isDeleted", "==", false)
            );
            const querySnapshot = await getDocs(q);
            const documents = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            return { success: true, data: documents };
        } catch (error) {
            console.error("Error fetching documents by participation:", error);
            return { success: false, error: error.message };
        }
    }
}

export default DocumentApi;
